import { ApolloClient } from '@apollo/client'
import { createSignal } from '@react-rxjs/utils'
import { combineLatest, filter, from, map, Observable, of, take, tap } from 'rxjs'
import { catchError, switchMap } from 'rxjs/operators'

import type {
  CartFragment,
  CountryCodeEnum,
  SetPaymentMethodOnCartMutation,
  SetPaymentMethodOnCartMutationVariables,
} from '../../../graphql/magento'
import {
  CreateCustomerCardDocument,
  SetPaymentMethodOnCartDocument,
  TokenBaseCardsDocument,
} from '../../../graphql/magento'
import { isNotNull } from '../../../utils/collectionTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { mapRight, switchMapRight, tapRight } from '../../../utils/rx/operators'
import { latestAccessToken$, latestAuthState$ } from '../../auth/state'
import { cyberSourceCardTypeFromNumber } from '../../cybersource/card-type'
import { cyberSourceFlexApiTokenizeRequest } from '../../cybersource/flex-api'
import { invalidateFlexKey } from '../../cybersource/flex-key'
import { latestApolloClient$ } from '../../graphql/apollo'
import { cardTypeFromCardNumberResult } from '../../m2/cybersource'
import { CardTypeInvalidError, UnexpectedCartError } from '../errors'
import { paymentMethodFromFieldValues } from '../payment'
import { ValidFormValues } from '../types'
import {
  billingAddressUpdateParamsIfNeeded,
  fromUpdateBillingAddress,
  fromUpdateBillingAddressAndPaymentMethod,
  UpdateBillingAddressIfNeededInput,
} from './billing'
import {
  createPlaceOrderTaskForCreditCard,
  createVanillaPlaceOrderTask,
  setPlaceOrderTaskInput,
} from './order'

// define input for updating both billing address and payment method
interface UpdatePaymentDetailsInput
  // `addressValues` from `UpdateBillingAddressIfNeededInput` is a subset of `ValidFormValues.PaymentDetails`
  extends Omit<UpdateBillingAddressIfNeededInput, 'addressValues'> {
  fieldValues: ValidFormValues.PaymentDetails | ValidFormValues.NoPaymentDetailsRequired
  dispensarySlug: string
}

// define input for combined billing address and payment method update
const [updatePaymentDetailsInput$, setUpdatePaymentDetailsInput] =
  createSignal<UpdatePaymentDetailsInput>()

export { setUpdatePaymentDetailsInput }

const tokenizeCard$ = (
  fieldValues: ValidFormValues.CreditCard,
): Observable<{ _tag: 'Left'; error: Error } | { _tag: 'Right'; data: string }> => {
  const cardType = cyberSourceCardTypeFromNumber(fieldValues.cardNumber)
  return !cardType
    ? of({ _tag: 'Left', error: new CardTypeInvalidError('CyberSource card type not resolved') })
    : cyberSourceFlexApiTokenizeRequest({
        cardNumber: fieldValues.cardNumber,
        cardType,
        cardExpirationMonth: fieldValues.cardExpMonth,
        cardExpirationYear: fieldValues.cardExpYear,
      }).pipe(
        tapRight(() => {
          invalidateFlexKey()
        }),
        mapRight(({ data }) => ({ _tag: 'Right' as const, data: data.token })),
      )
}

const createCustomerCardFactory$ = (values: ValidFormValues.CreditCard, cyberSourceToken: string) =>
  combineLatest([
    latestApolloClient$,
    latestAccessToken$.pipe(filter(isNotNull)),
    latestAuthState$.pipe(
      map(({ user }) => user),
      filter(isNotNull),
    ),
  ]).pipe(
    take(1),
    switchMap(([client, token, user]) => {
      const cardTypeResult = cardTypeFromCardNumberResult(values.cardNumber)
      if (cardTypeResult._tag === 'Left') {
        return of(cardTypeResult)
      }
      const { data: cardType } = cardTypeResult
      const ccLast4 = values.cardNumber.slice(-4)

      return from(
        client.mutate({
          mutation: CreateCustomerCardDocument,
          variables: {
            input: {
              active: true,
              address: {
                city: values.city,
                country_code: values.country_code as CountryCodeEnum,
                country_id: values.country_code as CountryCodeEnum,
                firstname: values.firstname,
                lastname: values.lastname,
                postcode: values.postcode,
                street: [values.street1],
                // M2 does not resolve `region_id` when submitting this mutation. Consequently, we
                // store the textual value and resolve to region options when used.
                region: { region: values.region, region_code: values.region },
                telephone: values.telephone,
              },
              additional: {
                cc_type: cardType,
                cc_last4: ccLast4,
                cc_exp_month: values.cardExpMonth,
                cc_exp_year: values.cardExpYear,
                save: values.saveCard,
              },
              customer_email: user?.email ?? '',
              method: 'paradoxlabs_cybersource',
              payment_id: cyberSourceToken,
            },
          },
          context: {
            token,
          },
          errorPolicy: 'none',
        }),
      ).pipe(
        // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
        map(({ data }) => ({ _tag: 'Right' as const, data })),
        tap(({ data }) => {
          const query = TokenBaseCardsDocument
          const cachedQuery = client.cache.readQuery({ query })
          // add new card to cached collection
          const newCards = [
            ...(cachedQuery?.tokenBaseCards ?? []),
            ...(data?.createTokenBaseCard ? [data.createTokenBaseCard] : []),
          ]
          client.cache.writeQuery({
            query,
            data: {
              tokenBaseCards: newCards,
            },
          })
        }),
        catchError(coerceCaughtToLeft),
      )
    }),
  )

const paymentMethodUpdateParamsIfNeeded = ({
  paymentDetails,
  customerCart,
}: {
  paymentDetails: ValidFormValues.PaymentDetails | ValidFormValues.NoPaymentDetailsRequired
  customerCart: CartFragment
}): SetPaymentMethodOnCartMutationVariables['paymentMethod'] | undefined => {
  const paymentMethod = paymentMethodFromFieldValues(paymentDetails)
  return paymentMethod.code === 'free' && customerCart.selected_payment_method?.code === 'free'
    ? undefined
    : // if payment method is purchase order and the P/O number hasn't changed, return undefined
      paymentMethod.code === 'purchaseorder' &&
        customerCart.selected_payment_method?.code === 'purchaseorder' &&
        customerCart.selected_payment_method?.purchase_order_number ===
          paymentMethod.purchase_order_number
      ? undefined
      : paymentMethod
}

const paymentMethodUpdateFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: SetPaymentMethodOnCartMutationVariables,
) =>
  from(
    client.mutate({
      mutation: SetPaymentMethodOnCartDocument,
      variables,
      context: {
        token,
      },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    catchError(coerceCaughtToLeft),
  )

const fromUpdatePaymentMethod = (
  variables: SetPaymentMethodOnCartMutationVariables,
): Observable<
  | { _tag: 'Left'; error: Error }
  | { _tag: 'Right'; data: SetPaymentMethodOnCartMutation | null | undefined }
> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    take(1),
    switchMap(([client, token]) => paymentMethodUpdateFactory(client, token, variables)),
  )

const updateBillingAddressAndPaymentMethodAsNeeded = ({
  fieldValues,
  customerAddresses,
  customerCart,
}: UpdatePaymentDetailsInput): Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: unknown }
> => {
  const billingAddressUpdateParams = billingAddressUpdateParamsIfNeeded({
    addressValues: fieldValues,
    customerAddresses,
    customerCart,
  })
  const paymentMethodUpdateParams = paymentMethodUpdateParamsIfNeeded({
    customerCart,
    paymentDetails: fieldValues,
  })

  return billingAddressUpdateParams && paymentMethodUpdateParams
    ? fromUpdateBillingAddressAndPaymentMethod({
        cartId: customerCart.id,
        billingAddress: billingAddressUpdateParams,
        paymentMethod: paymentMethodUpdateParams,
      })
    : billingAddressUpdateParams
      ? fromUpdateBillingAddress({
          cartId: customerCart.id,
          billingAddress: billingAddressUpdateParams,
        })
      : paymentMethodUpdateParams
        ? fromUpdatePaymentMethod({
            cartId: customerCart.id,
            paymentMethod: paymentMethodUpdateParams,
          })
        : of({ _tag: 'Right' as const, data: undefined })
}

/**
 * If customer entered new CC, tokenize and create corresponding card in Magento. Update M2 as
 * needed with new billing address and payment method. Finally prepare task for placing order
 * with given payment details. This binds sensitive information (e.g. CVV) for delayed execution
 * since CVV is not stored on the server.
 */
export const updatePaymentDetailsAndSetPlaceOrderTask$: Observable<
  { _tag: 'Left'; error: Error } | { _tag: 'Right'; data: { customerCart: CartFragment } }
> = updatePaymentDetailsInput$.pipe(
  // tokenize CC if needed
  switchMap(({ fieldValues, ...rest }) =>
    fieldValues.code !== 'paradoxlabs_cybersource' || fieldValues.selectedSavedCreditCardId
      ? of({ _tag: 'Right' as const, data: { fieldValues, ...rest } })
      : tokenizeCard$(fieldValues).pipe(
          switchMapRight(({ data: cyberSourceToken }) =>
            createCustomerCardFactory$(fieldValues, cyberSourceToken),
          ),
          mapRight(({ data }) => {
            const newSubscriptionId = data?.createTokenBaseCard?.hash
            return !newSubscriptionId
              ? {
                  _tag: 'Left' as const,
                  error: new UnexpectedCartError(
                    'Subscription ID not returned after tokenizing card',
                  ),
                }
              : {
                  _tag: 'Right' as const,
                  data: {
                    ...rest,
                    fieldValues: {
                      ...fieldValues,
                      // replace `new` placeholder with token
                      subscriptionId: newSubscriptionId,
                    },
                  },
                }
          }),
        ),
  ),
  switchMapRight(({ data: { customerAddresses, customerCart, fieldValues, dispensarySlug } }) =>
    updateBillingAddressAndPaymentMethodAsNeeded({
      customerAddresses,
      customerCart,
      fieldValues,
      dispensarySlug,
    }).pipe(
      mapRight(() => ({
        _tag: 'Right' as const,
        data: { customerCart, fieldValues, dispensarySlug },
      })),
    ),
  ),
  tap((result) => {
    if (result._tag === 'Left') {
      setPlaceOrderTaskInput(undefined)
      return
    }
    const {
      data: { customerCart, fieldValues, dispensarySlug },
    } = result
    // build place order task
    const taskInput =
      fieldValues.code !== 'paradoxlabs_cybersource'
        ? createVanillaPlaceOrderTask({ cartId: customerCart.id, dispensarySlug })
        : createPlaceOrderTaskForCreditCard({
            cartId: customerCart.id,
            paymentMethod: paymentMethodFromFieldValues(fieldValues),
          })
    setPlaceOrderTaskInput(taskInput)
  }),
  // build place order task
  mapRight(({ data: { customerCart } }) => ({
    _tag: 'Right' as const,
    data: { customerCart },
  })),
)
